import PromptPanel from '../components/agents/PromptPanel';
import { Button } from '../components/ui';
import { useUI } from '../context/UIContext';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { agentLabel } from '../lib/format';
import AgentEditor from '../components/agents/AgentEditor';
import { useAuth } from '../context/AuthContext';
import './AgentsPage.css';

const DEPARTMENT_TITLES = {
  analytics_lead: 'Аналитический отдел',
  research_lead: 'Исследовательский отдел',
  business_lead: 'Отдел бизнес-анализа',
  dev_lead: 'Отдел разработки',
  hardware_lead: 'Подотдел аппаратной разработки',
  quality_lead: 'Отдел контроля качества',
  editorial_lead: 'Отдел документации',
};
const DEPARTMENT_ORDER = Object.keys(DEPARTMENT_TITLES);
const profileLabel = profile => ({ planner: 'Планирование', drafter: 'Подготовка материалов', compactor: 'Сжатие контекста' }[profile] || profile);
const assignmentTarget = slug => slug === 'developer' ? 'developer' : `agent:${slug}`;

/**
 * Состав агентов: кто есть, из чего он сделан и что из этого правится.
 *
 * Кодовые агенты показаны вместе со строчными — и намеренно только на чтение.
 * Отдельная вкладка «кодовые» читалась бы как «эти можно править где-то ещё»,
 * а суть ровно обратная: спека не подменяет код. Строка с именем
 * `financial_modeler` заменила бы детерминированный расчёт генератором прозы,
 * и ниже по потоку выдуманное число неотличимо от вычисленного.
 *
 * «Перечитать» не толкает воркеров. У prefork-воркера N процессов, и рассылка
 * дошла бы до одного, а остальные продолжили бы отдавать старые промпты —
 * молча, что хуже отсутствия перезагрузки. Каждый процесс перечитывает сам
 * перед задачей, сравнивая revision; кнопка валидирует строки и показывает,
 * к какому составу воркеры сойдутся.
 */

export default function AgentsPage() {
  const { confirm } = useUI();
  const { user } = useAuth();
  const [showPrompts, setShowPrompts] = useState(false);
  const [bundleRevision, setBundleRevision] = useState(null);
  useEffect(() => { if (user?.is_admin) api.getPrompts().then(value => setBundleRevision(value.revision)).catch(() => {}); }, [user?.is_admin]);
  const [roster, setRoster] = useState(null);
  const [connections, setConnections] = useState({ connections: [], assignments: {} });
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(null);   // {slug} | {slug: null}
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.getAgentRoster()
      .then(setRoster)
      .catch(e => setError(e?.response?.data?.detail || 'Не удалось загрузить состав агентов'));
  }, []);

  useEffect(load, [load]);
  useEffect(() => {
    if (user?.is_admin) api.listConnections().then(setConnections).catch(() => {});
  }, [user?.is_admin]);

  const assignModel = async (slug, connectionId) => {
    try {
      await api.assignConnection(assignmentTarget(slug), connectionId);
      setConnections(await api.listConnections());
      setNotice(`Модель агента «${slug}» обновлена.`);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось назначить модель');
    }
  };

  const modelSelector = (slug, fallback) => user?.is_admin && slug !== 'experimenter' && <div className="form-group agents-model">
    <label className="form-label" htmlFor={`model-${slug}`}>{slug === 'developer' ? 'Инструмент разработчика' : 'Модель агента'}</label>
    <select id={`model-${slug}`} className="form-input" value={connections.assignments[assignmentTarget(slug)] || ''}
      onChange={e => assignModel(slug, e.target.value)}>
      <option value="">{slug === 'developer' ? 'Не назначен' : `По профилю${fallback ? `: ${profileLabel(fallback)}` : ''}`}</option>
      {connections.assignments[assignmentTarget(slug)] && !connections.connections.some(c => c.id === connections.assignments[assignmentTarget(slug)] && (c.available ?? c.enabled)) && <option value={connections.assignments[assignmentTarget(slug)]} disabled>{connections.connections.find(c => c.id === connections.assignments[assignmentTarget(slug)])?.name || 'Модель'} · Недоступна</option>}
      {connections.connections.filter(c => (c.available ?? c.enabled) && (slug === 'developer' ? ['acp', 'legacy_cli'] : ['llm', 'acp']).includes(c.kind))
        .map(c => <option value={c.id} key={c.id}>{c.provider_name ? `${c.provider_name} / ` : ''}{c.name}</option>)}
    </select>
  </div>;

  const reload = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const result = await api.reloadAgents();
      setRoster(result);
      setNotice(
        `Строки перечитаны: ${result.agents.length} агентов из спек, ` +
        `${result.code_agents.length} из кода. Воркеры подхватят ${result.applies_to_workers}.`
      );
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось перечитать спеки');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (agent) => {
    if (!await confirm(
      `Убрать агента «${agent.display_name}» из состава?\n\n` +
      'Артефакты и история останутся: они ссылаются на него по имени, и ' +
      'траектория, автор половины которой перестал существовать, нечитаема.'
    )) return;
    try {
      await api.archiveAgentSpec(agent.slug, bundleRevision);
      setBundleRevision((await api.getPrompts()).revision);
      setNotice(`Агент «${agent.display_name}» убран из состава`);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Не удалось убрать агента');
    }
  };

  const catalog = useMemo(() => {
    const agents = new Map((roster?.agents || []).map(agent => [agent.slug, { ...agent, kind: 'spec' }]));
    for (const agent of roster?.code_agent_details || []) {
      agents.set(agent.slug, { ...agent, display_name: agentLabel(agent.slug), kind: 'code' });
    }
    const departments = roster?.departments || [];
    const needle = query.trim().toLowerCase();
    const matches = agent => [agent.slug, agent.display_name, agent.artifact_type]
      .some(value => (value || '').toLowerCase().includes(needle));
    const sortDepartments = (a, b) => {
      const rank = slug => DEPARTMENT_ORDER.includes(slug) ? DEPARTMENT_ORDER.indexOf(slug) : DEPARTMENT_ORDER.length;
      return rank(a.slug) - rank(b.slug) || a.slug.localeCompare(b.slug);
    };
    const buildDepartment = (department, parentMatches = false) => {
      const title = DEPARTMENT_TITLES[department.slug] || agentLabel(department.slug);
      const head = agents.get(department.slug);
      const groupMatches = parentMatches || !needle || `${title} ${department.slug} ${head?.display_name || ''}`.toLowerCase().includes(needle);
      const allMembers = [...agents.values()].filter(agent => agent.department === department.slug && !agent.is_department_head);
      const members = allMembers.filter(agent => groupMatches || matches(agent))
        .sort((a, b) => a.display_name.localeCompare(b.display_name, 'ru'));
      const children = departments.filter(child => child.parent === department.slug)
        .sort(sortDepartments).map(child => buildDepartment(child, groupMatches)).filter(Boolean);
      if (!groupMatches && !members.length && !children.length && !(head && matches(head))) return null;
      return { ...department, title, head, members, memberCount: allMembers.length, children };
    };
    return {
      total: agents.size,
      departments: departments.filter(department => !department.parent).sort(sortDepartments)
        .map(department => buildDepartment(department)).filter(Boolean),
      coordinators: [...agents.values()].filter(agent => !agent.department && !agent.is_department_head && matches(agent)),
    };
  }, [roster, query]);

  const renderAgent = (agent, isHead = false, nested = false) => {
    const Heading = nested ? 'h4' : 'h3';
    return (
    <article key={agent.slug} className={`card agents-card${isHead ? ' agents-card-head' : ''}`} data-agent={agent.slug}>
      <div className="card-header">
        <div>
          {isHead && <div className="agents-role">Руководитель {agent.department ? 'подотдела' : 'отдела'}</div>}
          <Heading className="card-title">{agent.display_name}</Heading>
          <div className="text-muted agents-meta"><code>{agent.slug}</code>{agent.version ? ` · v${agent.version}` : ''}</div>
        </div>
        <span className={`badge ${agent.kind === 'code' ? 'badge-todo' : agent.is_builtin ? 'badge-done' : 'badge-running'}`}>
          {agent.kind === 'code' ? 'Системный' : agent.is_builtin ? 'Встроенный' : 'Свой'}
        </span>
      </div>
      {agent.artifact_type && <p className="text-muted">Результат: <code>{agent.artifact_type}</code></p>}
      {agent.tools?.length > 0 && <details className="agents-tools text-muted">
        <summary>Инструменты: {agent.tools.length}</summary>
        <p>{agent.tools.join(', ')}</p>
        {agent.tools.includes('search_web') && <p>Поиск в интернете. В режиме «Закрытая база» недоступен.</p>}
        {agent.tools.includes('fetch_source') && <p>Открытие и чтение источников. В режиме «Закрытая база» недоступно.</p>}
      </details>}
      {agent.tools_enabled === false && <p className="text-muted">Инструменты отключены в настройках платформы.</p>}
      {agent.session_tools?.length > 0 && <p className="text-muted">Документы сессии: доступны для чтения при наличии вложений.</p>}
      {agent.optional_mcp_tools?.length > 0 && <details className="agents-tools text-muted">
        <summary>Дополнительные MCP-инструменты: {agent.optional_mcp_tools.length}</summary>
        <p>{agent.optional_mcp_tools.join(', ')}</p>
        <p>Требуют подключения соответствующих MCP-серверов. Доступность проверяется при запуске агента.</p>
      </details>}
      {agent.slug === 'experimenter' && <p className="text-muted">Выполняет измерительные скрипты. Языковая модель не требуется.</p>}
      {modelSelector(agent.slug, agent.model_profile)}
      {agent.kind === 'spec' && <div className="flex gap-2 mt-2">
        <Button className="btn btn-secondary btn-sm" onClick={() => setEditing({ slug: agent.slug })}>
          {agent.is_builtin ? 'Посмотреть' : 'Править'}
        </Button>
        {!agent.is_builtin && agent.stored && <Button className="btn btn-danger btn-sm" onClick={() => archive(agent)}>Убрать</Button>}
      </div>}
    </article>
    );
  };

  const renderDepartment = (department, nested = false) => (
    <section key={department.slug} className={`agents-department${nested ? ' agents-subdepartment' : ''}`}
      data-department={department.slug} aria-labelledby={`department-${department.slug}`}>
      <div className="agents-department-header">
        {nested ? <h3 id={`department-${department.slug}`} className="card-title">{department.title}</h3>
          : <h2 id={`department-${department.slug}`} className="card-title">{department.title}</h2>}
        <span className="text-muted">Сотрудников: {department.memberCount}{department.children.length ? ` · Подотделов: ${department.children.length}` : ''}</span>
      </div>
      <div className="agents-grid">
        {department.head && renderAgent(department.head, true, nested)}
        {department.members.map(agent => renderAgent(agent, false, nested))}
      </div>
      {department.children.map(child => renderDepartment(child, true))}
    </section>
  );

  const onSaved = (saved, meta) => {
    setNotice(meta?.forked
      ? `Создан форк «${saved.slug}» — теперь его можно править`
      : `Агент «${saved.slug}» сохранён (v${saved.version}). ` +
        'Воркеры подхватят его на следующей задаче.');
    load();
    // После форка сразу открываем копию: иначе человек остаётся в
    // заблокированном редакторе оригинала и должен искать форк сам.
    setEditing(meta?.forked ? { slug: saved.slug } : null);
  };

  if (error && !roster) return <div className="alert alert-error">{error}</div>;
  if (!roster) return <div className="loading">Загрузка состава агентов…</div>;

  if (editing) {
    return (
      <>
        <div className="page-header">
          <button className="back-link" onClick={() => { setEditing(null); load(); }}>
            ← К составу агентов
          </button>
        </div>
        <AgentEditor
          slug={editing.slug}
          options={roster.options}
          onClose={() => { setEditing(null); load(); }}
          onSaved={onSaved}
        />
      </>
    );
  }

  if (showPrompts) return <div><Button onClick={() => { setShowPrompts(false); load(); api.getPrompts().then(value => setBundleRevision(value.revision)); }}>К составу агентов</Button><PromptPanel /></div>;

  return (
    <>
      <Button className="btn btn-secondary" onClick={() => setShowPrompts(true)}>Инструкции всех ролей</Button>
      <div className="page-header agents-page-header">
        <h1 className="page-title">Агенты</h1>
        <div className="flex gap-2">
          <Button className="btn btn-secondary" disabled={busy} onClick={reload}>
            Перечитать
          </Button>
          <Button className="btn btn-primary" onClick={() => setEditing({ slug: null })}>
            Новый агент
          </Button>
        </div>
      </div>

      <p className="text-muted agents-intro">Руководители и сотрудники сгруппированы по отделам. Модель можно назначить каждому агенту отдельно.</p>

      {notice && <div className="alert alert-info">{notice}</div>}
      {error && <div className="alert alert-error">{error}</div>}
      {roster.errors?.length > 0 && (
        <div className="alert alert-warning">
          <strong>Строки, не прошедшие валидацию</strong> — они пропущены, остальные
          установлены: одна битая строка не должна снимать с работы остальные.
          <ul style={{ margin: '6px 0 0', paddingLeft: '1.1rem' }}>
            {roster.errors.map(e => <li key={e}><code>{e}</code></li>)}
          </ul>
        </div>
      )}

      <div className="agents-toolbar">
        <input
          className="form-input" value={query} aria-label="Поиск агентов и отделов"
          placeholder="Поиск по агентам и отделам"
          onChange={e => setQuery(e.target.value)}
        />
        <span className="text-muted" style={{ fontSize: '0.82rem' }}>
          Всего агентов: {catalog.total}
        </span>
      </div>

      {catalog.coordinators.length > 0 && <section className="agents-department" aria-labelledby="agents-coordination">
        <h2 id="agents-coordination" className="card-title agents-coordination-title">Координация работы</h2>
        <div className="agents-grid">{catalog.coordinators.map(agent => renderAgent(agent))}</div>
      </section>}
      {catalog.departments.map(department => renderDepartment(department))}
      {!catalog.departments.length && !catalog.coordinators.length && <div className="empty">Ничего не найдено.</div>}
    </>
  );
}
